let drawer;
let opener;
const bundleStatuses = new WeakMap();

const currencyFormatter = (cart) => {
  const currency = cart.currency || 'USD', locale = document.documentElement.lang || 'en-US';
  const standard = new Intl.NumberFormat(locale, {style: 'currency', currency});
  if (!['USD', 'AUD', 'CAD', 'GBP', 'NZD'].includes(currency)) return standard;
  const whole = new Intl.NumberFormat(locale, {style: 'currency', currency, minimumFractionDigits: 0});
  return {format: value => (Number.isInteger(value) ? whole : standard).format(value)};
};

function cartDrawer() {
  drawer ||= document.querySelector('[data-cart-drawer]');
  return drawer;
}

function updateCount(itemCount) {
  document.querySelectorAll('[data-cart-count]').forEach((count) => {
    count.textContent = itemCount;
    count.hidden = itemCount === 0;
  });
}

function appendNativePrice(node, original, final, formatter) {
  if (original > final) {
    const regular = document.createElement('s');
    regular.textContent = formatter.format(original / 100);
    regular.setAttribute('aria-label', `Regular price ${regular.textContent}`);
    node.append(regular, ' ');
  }
  const current = document.createElement('span');
  current.textContent = formatter.format(final / 100);
  if (original > final) current.setAttribute('aria-label', `Discounted price ${current.textContent}`);
  node.append(current);
}

function discountLabels(item) {
  const labels = new Set();
  // Ajax exposes product allocations separately from legacy order allocations.
  [...(item.discount_allocations || []), ...(item.line_level_discount_allocations || []), ...(item.discounts || [])].forEach(discount => {
    const title = discount.discount_application?.title || discount.title;
    if (title && Number(discount.amount) > 0) labels.add(title);
  });
  return [...labels];
}

function makeItem(item, formatter) {
  const article = document.createElement('article');
  article.className = 'he-cart-drawer__item';
  const media = document.createElement(item.image ? 'img' : 'div');
  media.className = item.image ? 'he-cart-drawer__image' : 'he-cart-drawer__image he-cart-drawer__image--empty';
  if (item.image) {
    media.src = item.image;
    media.alt = item.product_title;
    media.loading = 'lazy';
  }
  else media.textContent = item.product_title;
  const copy = document.createElement('div');
  const title = document.createElement('h3'); title.textContent = item.product_title;
  const variant = document.createElement('p');
  variant.textContent = item.variant_title === 'Default Title' ? 'Standard selection' : item.variant_title;
  const price = document.createElement('p');
  price.className = 'he-cart-drawer__item-price';
  appendNativePrice(price, item.original_price ?? item.price, item.final_price ?? item.price, formatter);
  price.append(' each');
  const lineTotal = document.createElement('p');
  lineTotal.className = 'he-cart-drawer__line-total';
  lineTotal.textContent = 'Line total ';
  appendNativePrice(lineTotal, item.original_line_price, item.line_price ?? item.final_line_price, formatter);
  const discounts = document.createElement('ul');
  discounts.className = 'he-sale-cart-discounts';
  const labels = discountLabels(item);
  labels.forEach(label => {
    const entry = document.createElement('li');
    entry.className = 'he-sale-cart-discount';
    entry.textContent = label;
    discounts.append(entry);
  });
  discounts.hidden = labels.length === 0;
  const controls = document.createElement('div');
  controls.className = 'he-cart-drawer__quantity';
  controls.dataset.cartLineKey = item.key;
  controls.dataset.cartLineQuantity = String(item.quantity);
  const decrease = document.createElement('button');
  decrease.type = 'button';
  decrease.setAttribute('data-cart-quantity-action', 'decrease');
  decrease.setAttribute('aria-label', `Decrease quantity for ${item.product_title}`);
  decrease.textContent = '−';
  const amount = document.createElement('span');
  amount.textContent = item.quantity;
  amount.setAttribute('aria-label', `Quantity ${item.quantity}`);
  const increase = document.createElement('button');
  increase.type = 'button';
  increase.setAttribute('data-cart-quantity-action', 'increase');
  increase.setAttribute('aria-label', `Increase quantity for ${item.product_title}`);
  increase.textContent = '+';
  const remove = document.createElement('button');
  remove.type = 'button';
  remove.setAttribute('data-cart-remove', '');
  remove.textContent = 'Remove';
  remove.setAttribute('aria-label', `Remove ${item.product_title} from cart`);
  controls.append(decrease, amount, increase, remove);
  copy.append(title, variant, price, lineTotal, discounts, controls);
  article.append(media, copy);
  return article;
}

export function renderCart(cart) {
  const bundleStatus = bundleStatuses.get(cart);
  if (bundleStatus && !bundleStatus.isCurrent()) return;
  const root = cartDrawer();
  if (!root) return;
  const items = root.querySelector('[data-cart-drawer-items]');
  const empty = root.querySelector('[data-cart-drawer-empty]');
  const summary = root.querySelector('[data-cart-drawer-summary]');
  const subtotal = root.querySelector('[data-cart-drawer-subtotal]');
  const status = root.querySelector('[data-cart-drawer-status]');
  const formatter = currencyFormatter(cart);
  const isEmpty = cart.item_count === 0;
  items.hidden = isEmpty;
  empty.hidden = !isEmpty;
  summary.hidden = isEmpty;
  items.replaceChildren();
  if (!isEmpty) cart.items.forEach((item) => items.append(makeItem(item, formatter)));
  subtotal.textContent = formatter.format(cart.total_price / 100);
  status.textContent = isEmpty
    ? 'Your cart is empty.'
    : `${cart.item_count} piece${cart.item_count === 1 ? '' : 's'} in your cart.`;
  updateCount(cart.item_count);
  const bundle = root.querySelector('[data-cart-bundle-status]');
  if (bundle) {
    const message = bundleStatus?.html
      ? new DOMParser().parseFromString(bundleStatus.html, 'text/html').querySelector('[data-cart-bundle-message]')
      : null;
    bundle.replaceChildren(...(message ? [message] : []));
    bundle.hidden = !message;
  }
  clearDrawerError();
}

export function openDrawer(source) {
  const root = cartDrawer();
  if (!root) return;
  opener = source || document.activeElement;
  root.hidden = false;
  requestAnimationFrame(() => root.classList.add('is-open'));
  document.body.style.overflow = 'hidden';
  root.querySelector('[data-cart-drawer-close]')?.focus();
}

export function closeDrawer() {
  const root = cartDrawer();
  if (!root || root.hidden) return;
  root.classList.remove('is-open');
  document.body.style.overflow = '';
  window.setTimeout(() => { if (!root.classList.contains('is-open')) root.hidden = true; }, 280);
  opener?.focus?.();
}

async function cartJson(response, fallback) {
  const payload = await response.json();
  if (!response.ok) throw new Error(payload.description || payload.message || fallback);
  return payload;
}

export function createCartClient({ fetchImpl = fetch, routes = {}, bundleTimeoutMs = 1500 } = {}) {
  const cartUrl = routes.cart || '/cart.js';
  const addUrl = routes.add || '/cart/add.js';
  const changeUrl = routes.change || '/cart/change.js';
  const sectionUrl = `${cartUrl.replace(/\.js(?:\?.*)?$/, '')}?section_id=cart-bundle-status`;
  let revision = 0;
  let latestSuccessfulRevision = 0;
  let pendingCart = Promise.resolve();
  // Product tags are available to Liquid, but absent from the Ajax cart payload.
  const withBundleStatus = async (cart, requestRevision) => {
    let html = null;
    let timeout;
    const controller = new AbortController();
    try {
      const refresh = (async () => {
        const response = await fetchImpl(sectionUrl, {
          headers: { Accept: 'text/html' }, cache: 'no-store', signal: controller.signal,
        });
        return response.ok ? response.text() : null;
      })();
      // Bound the complete display refresh, including a stalled response body.
      html = await Promise.race([refresh, new Promise((resolve) => {
        timeout = setTimeout(() => { controller.abort(); resolve(null); }, bundleTimeoutMs);
      })]);
    } catch {
      // A failed display refresh must not turn a successful cart change into an error.
    } finally {
      clearTimeout(timeout);
    }
    bundleStatuses.set(cart, { html, isCurrent: () => requestRevision === latestSuccessfulRevision });
    return cart;
  };
  const read = async () => cartJson(
    await fetchImpl(cartUrl, { headers: { Accept: 'application/json' } }),
    'We could not refresh your cart. Please try again.',
  );
  const request = (operation) => {
    const requestRevision = ++revision;
    // Reads must follow pending mutations; section responses can arrive independently.
    const result = pendingCart.then(operation);
    pendingCart = result.catch(() => undefined);
    return result.then((cart) => {
      latestSuccessfulRevision = requestRevision;
      return withBundleStatus(cart, requestRevision);
    });
  };
  return {
    read: () => request(read),
    addForm: (formData) => request(async () => {
      await cartJson(await fetchImpl(addUrl, {
        method: 'POST', headers: { Accept: 'application/json' }, body: formData,
      }), 'We could not add this piece to your cart. Please try again.');
      return read();
    }),
    addItems: (payload) => request(async () => {
      await cartJson(await fetchImpl(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      }), 'We could not add this piece to your cart. Please try again.');
      return read();
    }),
    changeLine: (id, quantity) => request(async () => cartJson(await fetchImpl(changeUrl, {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity }),
      }), 'We could not update your cart. Please try again.')),
  };
}

const client = createCartClient();
let mutationPromise = Promise.resolve();

function showDrawerError(message) {
  const root = cartDrawer();
  const error = root?.querySelector('[data-cart-drawer-error]');
  const copy = root?.querySelector('[data-cart-drawer-error-copy]');
  if (!error || !copy) return;
  copy.textContent = message;
  error.hidden = false;
}

function clearDrawerError() {
  const error = cartDrawer()?.querySelector('[data-cart-drawer-error]');
  if (error) error.hidden = true;
}

function changeLine(key, quantity) {
  const root = cartDrawer();
  const request = mutationPromise.then(async () => {
    root?.setAttribute('aria-busy', 'true');
    clearDrawerError();
    try {
      const cart = await client.changeLine(key, quantity);
      renderCart(cart);
      return cart;
    } catch (error) {
      showDrawerError(error.message);
      throw error;
    } finally {
      root?.removeAttribute('aria-busy');
    }
  });
  mutationPromise = request.catch(() => undefined);
  return request;
}

export async function fetchCart() {
  return client.read();
}

function finishAdd(cart, source) {
  renderCart(cart);
  openDrawer(source);
  document.dispatchEvent(new CustomEvent('cart:added', { detail: { cart } }));
  return cart;
}

export async function addToCart(formData, source) {
  return finishAdd(await client.addForm(formData), source);
}

export async function addItems(payload, source) {
  return finishAdd(await client.addItems(payload), source);
}

if (typeof window !== 'undefined') {
  window.HewnCartDrawer = { addToCart, addItems, fetchCart, openDrawer, closeDrawer };
}

function trapFocus(event) {
  const root = cartDrawer();
  if (event.key !== 'Tab' || !root || root.hidden) return;
  const focusable = [...root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])')]
    .filter((element) => !element.hidden && element.offsetParent !== null);
  if (focusable.length === 0) return;
  const first = focusable[0];
  const last = focusable.at(-1);
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function showFormError(form, message = '') {
  const error = form.querySelector('[data-cart-form-error]');
  if (!error) return;
  error.textContent = message;
  error.hidden = message.length === 0;
}

function initialiseCartDrawer() {
  const root = cartDrawer();
  if (!root || root.dataset.bound === 'true') return;
  root.dataset.bound = 'true';
  root.querySelectorAll('[data-cart-drawer-close]').forEach((button) => button.addEventListener('click', closeDrawer));
  root.addEventListener('click', (event) => {
    const action = event.target.closest?.('[data-cart-quantity-action],[data-cart-remove]');
    if (!action) return;
    const controls = action.closest('[data-cart-line-key]');
    const key = controls?.dataset.cartLineKey;
    const current = Number(controls?.dataset.cartLineQuantity || 0);
    if (!key) return;
    const quantity = action.hasAttribute('data-cart-remove')
      ? 0
      : Math.max(0, current + (action.dataset.cartQuantityAction === 'increase' ? 1 : -1));
    void changeLine(key, quantity).catch(() => undefined);
  });
  root.querySelector('[data-cart-drawer-retry]')?.addEventListener('click', async () => {
    root.setAttribute('aria-busy', 'true');
    try {
      renderCart(await fetchCart());
    } catch (error) {
      showDrawerError(error.message);
    } finally {
      root.removeAttribute('aria-busy');
    }
  });
  document.querySelectorAll('[data-cart-toggle]').forEach((toggle) => toggle.addEventListener('click', async (event) => {
    event.preventDefault();
    try { renderCart(await fetchCart()); openDrawer(toggle); }
    catch { window.location.assign(toggle.href); }
  }));
  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('.he-pdp__form')) return;
    if (form.querySelector('[data-pricing-size]')?.value === 'Custom size') return;
    event.preventDefault();
    const submit = form.querySelector('[data-add-to-cart]');
    if (!submit || submit.dataset.cartBusy === 'true') return;
    showFormError(form);
    submit.dataset.cartBusy = 'true';
    submit.disabled = true;
    submit.textContent = 'Adding…';
    try {
      await addToCart(new FormData(form), submit);
    } catch (error) {
      showFormError(form, error.message);
    } finally {
      delete submit.dataset.cartBusy;
      const option = form.querySelector('[data-variant-select]')?.selectedOptions?.[0];
      submit.disabled = option?.dataset.available !== 'true';
      submit.textContent = submit.disabled ? 'Sold out' : 'Add to cart';
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
    if (event.key === 'Tab') trapFocus(event);
  });
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialiseCartDrawer);
  else initialiseCartDrawer();
}
